import React from "react";
import { useNavigate } from "react-router-dom";
import ContinueButton from "@/components/Buttons/ContinueButton";
import currencySymbolMap from "currency-symbol-map";
import { FREE_TRIAL_DAYS } from "@/lib/domainConstants";

interface PremiumFooterProps {
  amount: string;
  platform: string;
  onContinue: () => void;
  period: "weekly" | "monthly";
  currency: string;
  showFreeTrialText: boolean;
}

const PremiumFooter: React.FC<PremiumFooterProps> = ({
  amount,
  platform,
  onContinue,
  period,
  currency,
  showFreeTrialText: isFreeTrial,
}) => {
  const nav = useNavigate();
  const currencySymbol = currencySymbolMap(currency) || "$";

  const getWeeklyPrice = (monthlyAmount: string): string => {
    const weeklyAmount = Math.floor((Number(monthlyAmount) / 4) * 100) / 100;
    return weeklyAmount.toFixed(2);
  };

  const getPricingText = () => {
    if (period === "weekly") {
      return `${currencySymbol}${amount}/week (Cancel anytime)`;
    }

    // Monthly subscription with trial
    if (isFreeTrial) {
      return `${FREE_TRIAL_DAYS} days FREE, then ${currencySymbol}${amount}/month (${currencySymbol}${getWeeklyPrice(
        amount
      )}/week)`;
    }

    // Regular monthly subscription
    return `${currencySymbol}${amount}/month (${currencySymbol}${getWeeklyPrice(
      amount
    )}/week)`;
  };

  const getButtonText = () => {
    if (isFreeTrial) {
      return `Start My ${FREE_TRIAL_DAYS} Free Days`;
    }
    return "Start Learning Today";
  };

  const getPlatformSettingsText = () => {
    return platform === "apple"
      ? "App Store settings"
      : "your account settings";
  };

  return (
    <div className={`flex flex-col gap-4 border-t border-[var(--color-grey-light)] ${platform === "web" ? "bottom-padding-web" : "pb-2"}`}>
      <div className="text-[10px] pt-1">
        Subscription auto-renews at the same price until canceled via{" "}
        {getPlatformSettingsText()}. See our{" "}
        <span
          onClick={() => nav("/terms/premium")}
          className="underline font-semibold cursor-pointer"
        >
          Terms
        </span>{" "}
        and{" "}
        <span
          onClick={() => nav("/privacy/premium")}
          className="underline font-semibold cursor-pointer"
        >
          Privacy Policy
        </span>
        .
      </div>
      <div>
        <ContinueButton text={getButtonText()} onClick={onContinue} />
        <div className="text-[10px] text-[var(--color-grey)] mt-1">
          {getPricingText()}
        </div>
      </div>
    </div>
  );
};

export default PremiumFooter;
